import { validateHex } from './colors';
import { extractParam } from './queryParams';
import { pyBool } from './pyBool';

/**
 * Parse theme + color config from the query params (P3-7 / MR-35).
 *
 * Extracted verbatim from app.vue's `processQueryParams`: the
 * `v3PlayerConfigs.theme` switch, the `v3PlayerConfigs.preferences` block, and
 * the `v4PlayerConfigs` color/vertical assignments. app.vue merges the returned
 * pieces into its config objects. Every value is byte-identical to the original
 * (same `validateHex`/`extractParam` calls, same dark-preset hexes, same XSS
 * lock: an invalid hex → `null`, which the v3 light path defaults away).
 *
 * The dark presets (#222046 / #ffffff / #532aa2) and the v3 light defaults
 * (#ffffff / #222046 / #532aa2) are preserved. Each v3 preference is true only
 * for the literal string 'true' (`galleryLimit` is `limit || 0`, raw — not
 * parsed).
 *
 * 🔴 `vertical` is the ONE exception, and it is deliberate (D-215). It reads
 * through `pyBool`, so it accepts BOTH 'true' and 'True'. It used to accept only
 * the lowercase spelling, which made this function disagree with the LOADER:
 * `classes/widget.js`'s sizing branch has always tested
 * `vertical === 'True' || vertical === 'true'`, so a Python-serialised
 * `vertical=True` sized the iframe 320x500 and then this reader returned false
 * and the widget painted the HORIZONTAL layout inside it — a 320-wide box around
 * a 700-wide design. MEASURED 2026-08-31: 46 loads/day carried 'True' against
 * 6,331 lowercase, so this was live-broken traffic, not a hypothetical.
 *
 * 🪤 Do NOT "make this consistent" by widening the v3 preferences to `pyBool`
 * too. They are lowercase-only because nothing is known to put the Python
 * spelling on the wire for them: no loader-side reader consumes them, so there is
 * no second reader to disagree with and no divergence to close. Widening a
 * grammar nothing speaks just invents a contract this repo would then have to
 * keep.
 *
 * 🔑 The criterion is a real CONSUMER that can disagree about the spelling.
 * `vertical` and `full` both qualify, but only ONE of them ever actually diverged:
 * `classes/widget.js` tests both spellings for each, and `vertical`'s widget-side
 * reader was lowercase-only, so the two sides of the iframe boundary disagreed
 * visibly — in iframe size, on live traffic. `full`'s widget-side reader was BORN
 * on `pyBool` at D-211, so no divergence ever shipped for it; it qualifies on the
 * criterion without ever having failed it. An earlier revision of this stanza said
 * "`vertical` and `full` ... disagreed", which is true of the pair only if you read
 * it as the criterion rather than the history. 🪤 `use_m4a` used to be named here as
 * the odd one out — no loader reader, dead downstream, normalised for consistency
 * rather than to fix a failure. D-220 deleted that parse on 2026-09-06, so it is no
 * longer a reader of anything and cannot be cited as precedent either way. The v3
 * preferences have neither a second reader nor a measured producer, so they stay as
 * they are.
 *
 * @param {Object<string,string>} queryParams parsed query params
 * @returns {{isDarkTheme:boolean, v3Theme:Object, v3Preferences:Object,
 *   v4Colors:Object, vertical:boolean}}
 */
export const parseTheme = (queryParams) => {
  const type = queryParams.theme || 'light';
  const isDarkTheme = type === 'dark';

  const v3Theme = { type };
  switch (type) {
    case 'dark':
      v3Theme.background = '#222046';
      v3Theme.text = '#ffffff';
      v3Theme.accent = '#532aa2';
      break;

    default:
      // light theme shouldn't have background/text/accent params, so they'll fallback to default
      v3Theme.background = validateHex(queryParams.background) || '#ffffff';
      v3Theme.text = validateHex(queryParams.text) || '#222046';
      v3Theme.accent = validateHex(queryParams.accent) || '#532aa2';
  }

  const v3Preferences = {
    enableAlbumArt: queryParams.albumart === 'true' || false,
    enableHashtags: queryParams.hashtags === 'true' || false,
    enableShowNotes: queryParams.shownotes === 'true' || false,
    enableGallery: queryParams.gallery === 'true' || false,
    galleryLimit: queryParams.limit || 0,
  };

  const v4Colors = {
    background: validateHex(extractParam(queryParams, 'background')),
    play_button: validateHex(extractParam(queryParams, 'play_button')),
    start_remaining: validateHex(extractParam(queryParams, 'start_remaining')),
  };

  const vertical = pyBool(queryParams.vertical);

  return {
    isDarkTheme, v3Theme, v3Preferences, v4Colors, vertical,
  };
};
